import { request } from './client';
import { FEATURES } from './features';
import { idbStorage, requestPersistentStorage } from './idbStorage';
import { createQueue, type QueueKind, type Sender } from './queue';
import type { ReportIssueRequestDto, ScanLogRequestDto, TransferConfirmRequestDto } from './types';

/**
 * Běhové zapojení fronty do aplikace.
 *
 * Odesílá se na popředí (Background Sync neumí Safari ani iOS):
 *   - při startu aplikace,
 *   - při návratu online,
 *   - při návratu na kartu / probuzení telefonu,
 *   - a jednou za minutu, dokud něco ve frontě je.
 *
 * Payloady jsou typované z generovaného schématu bez `clientActionId` —
 * ten doplňuje fronta z ID položky, aby byl klíč idempotence vždy tentýž
 * napříč opakovanými pokusy.
 */
export type ScanLogPayload = Omit<ScanLogRequestDto, 'clientActionId'>;
export type ReportIssuePayload = Omit<ReportIssueRequestDto, 'clientActionId'>;
export type TransferConfirmPayload = Omit<TransferConfirmRequestDto, 'clientActionId'>;

const senders: Partial<Record<QueueKind, Sender>> = {};

if (FEATURES.scanLog) {
  senders.scanLog = ((payload, id) =>
    request('/app/v2/scanner/scan-log', {
      body: { clientActionId: id, ...(payload as object) },
    }).then(() => undefined)) as Sender;
}

if (FEATURES.reportIssue) {
  senders.reportIssue = ((payload, id) =>
    request('/app/v2/scanner/report-issue', {
      body: { clientActionId: id, ...(payload as object) },
    }).then(() => undefined)) as Sender;
}

if (FEATURES.transferConfirm) {
  senders.transferConfirm = ((payload, id) =>
    request('/app/v2/scanner/transfer-confirm', {
      body: { clientActionId: id, ...(payload as object) },
    }).then(() => undefined)) as Sender;
}

export const queue = createQueue({ storage: idbStorage, senders });

let started = false;

export function startQueue(): void {
  if (started) return;
  started = true;

  void requestPersistentStorage();
  void queue.flush();

  // Vrátil se signál – nečekat na backoff.
  window.addEventListener('online', () => void queue.flush({ ignoreBackoff: true }));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void queue.flush({ ignoreBackoff: true });
  });

  // Záchranná síť pro případ, že událost `online` nedorazí (stává se
  // při přepnutí mezi AP ve skladu, kdy spojení „neupadne", jen chvíli nefunguje).
  setInterval(() => void queue.flush(), 60_000);
}

/** Zda je akce vůbec zapnutá – UI podle toho nemá slibovat nemožné. */
export function isEnabled(kind: QueueKind): boolean {
  return senders[kind] !== undefined;
}
